import saveAs from 'save-as';
import ApexCharts from 'apexcharts';

export function download(filename, filetype, data) {
    let blob = new Blob([data], { type: filetype });
    saveAs(blob, filename);
}

// for generated PDF in Analytics Graph
export const getPNGDataURI = async (id) => {
    // Wait up to ~2s for the chart to exist
    for (let i = 0; i < 20; i++) {
        const chart = ApexCharts.getChartByID(id);
        if (chart) return ApexCharts.exec(id, 'dataURI');
        await new Promise((r) => setTimeout(r, 100));
    }
    throw new Error(`Chart ${id} not found (not mounted yet)`);
};
export function parseQuestionId(questionId) {
    const result = /^((?:learning)|(?:reading)|(?:writing)|(?:oralcommunication)|(?:numeracy))-l(\d*)q(\d*)(a|b|c|d|e)$/.exec(questionId);
    if (!result) {
        throw new Error(`Error while attempting to parse QuestionId '${questionId}'`);
    }
    const [id, skill, level, number, variant] = result;
    return {
        id,
        skill,
        // $FlowFixMe:  Number enums don't support parseInt.
        level: parseInt(level, 10),
        // $FlowFixMe:  Number enums don't support parseInt.
        number: parseInt(number, 10),
        variant
    };
}

export const generateMongoLikeId = () => {
    const timestamp = Math.floor(new Date().getTime() / 1000).toString(16); // 4 bytes for timestamp
    const randomHex = 'xxxxxxxxxxxxxxxx'
        .replace(/[x]/g, () => {
            // 8 bytes for random data
            return Math.floor(Math.random() * 16).toString(16);
        })
        .toLowerCase();

    // Combine timestamp and random hex to create a 24-character hex string
    return timestamp + randomHex;
};

export const getCurrentWeekDateRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // Sunday is 0, Monday is 1, ..., Saturday is 6

    // Calculate the difference to get to Monday
    // If today is Sunday (0), subtract 6 days to get to the previous Monday.
    // Otherwise, subtract (dayOfWeek - 1) days to get to the current week's Monday.
    const diffToMonday = dayOfWeek === 0 ? -6 : -(dayOfWeek - 1);

    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0); // Set to the very beginning of Monday

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999); // Set to the very end of Sunday

    return {
        start: monday,
        end: sunday
    };
};

export function validatePasswordChars(str) {
    const result = {
        status: true,
        uppercase: false,
        lowercase: false,
        special: false,
        number: false
    };

    // Check for lowercase letters
    if (/[a-z]/.test(str)) result.lowercase = true;

    // Check for uppercase letters
    if (/[A-Z]/.test(str)) result.uppercase = true;

    // Check for digits
    if (/\d/.test(str)) result.number = true;

    // Keep this list aligned with the backend password policy.
    if (/[!@#$%^&*()_+=[\]{};:'",.<>/?\\|`~-]/.test(str)) result.special = true;

    // Determine if all conditions are met
    result.status = result.uppercase && result.lowercase && result.special && result.number;

    return result;
}

export const formatBytes = (bytes = 0) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

export const formatDate = (value) => {
    if (!value) return 'Recently uploaded';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recently uploaded';
    return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    }).format(date);
};

export const formatDateTime = (date) => {
    if (!date) return '';

    return new Intl.DateTimeFormat('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }).format(new Date(date));
};

export const normalizeSearchValue = (value = '') => String(value).toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

export const formatChronologicalDateTime = (date) => {
    if (!date) return '';

    const inputDate = new Date(date);
    const now = new Date();

    if (Number.isNaN(inputDate.getTime())) return '';

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const startOfInputDate = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

    const differenceInDays = (startOfToday - startOfInputDate) / (1000 * 60 * 60 * 24);

    const time = inputDate.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });

    if (differenceInDays === 0) {
        return `Today, ${time}`;
    }

    if (differenceInDays === 1) {
        return `Yesterday, ${time}`;
    }

    const dayMonth = inputDate.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short'
    });

    return `${dayMonth}, ${time}`;
};

/**
 * Get the difference in days between two dates
 * @param {Date|string} dateFrom
 * @param {Date|string} dateTo
 * @returns {number}
 */
export const getDaysDifference = (dateFrom, dateTo) => {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);

    // Get the difference in milliseconds
    const timeDifference = Math.abs(to.getTime() - from.getTime());

    // Milliseconds in one day
    const oneDay = 1000 * 60 * 60 * 24;

    // Get completed days
    return Math.floor(timeDifference / oneDay);
};

/**
 * Get human-readable elapsed time from a given date.
 */
export const getTimeAgo = (date) => {
    if (!date) return '';

    const inputDate = new Date(date);

    if (Number.isNaN(inputDate.getTime())) return '';

    const now = new Date();

    if (inputDate.getTime() > now.getTime()) return '';

    // Compare calendar dates using the browser's local timezone.
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

    const inputDay = Date.UTC(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

    const days = Math.floor((today - inputDay) / 86400000);

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';

    if (days < 7) {
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    if (days < 30) {
        const weeks = Math.floor(days / 7);
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }

    if (days < 365) {
        const months = Math.floor(days / 30);
        return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }

    const years = Math.floor(days / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
};
